import api from "./api";
import { toManualPaymentFormData } from "./paymentService";

// Lost / reprint request flow (Lost card -> director approval -> pay -> print
// -> issue), backed by IdCardReprintController. Distinct resource from the
// print/issue queue in idCardService.js — a reprint only ever *feeds into*
// that existing queue once paid (the backend flips card_status to "Pending
// Print" on payment confirm), so printing/issuing itself is unchanged and
// still goes through idCardService.js's listIdCardPrintRequests/getIdCardPdf/
// markIdCardIssued.
//
// Two independent callers hit `store` — same IdCardReprintController::store()
// method either way, but routed through two DIFFERENT paths, because each
// caller only has one of the two relevant permissions:
//   - A portal member (student/independent/coach token) submitting/tracking
//     their own reprint has "request_own_id_card_reprint" only — gated on
//     POST/GET `/api/member/id-card-reprint-requests*`. The backend resolves
//     member_id from their token and ignores any member_id field they send.
//   - A system user with "submit_id_card_reprint_requests" (e.g. Instructor)
//     submitting on behalf of a walk-in member has that permission but NOT
//     "request_own_id_card_reprint" — gated on POST `/api/id-card-reprint-
//     requests`, passing member_id explicitly.
// `submitIdCardReprintRequest` below picks the right URL based on whether the
// caller passed a member_id (system-user on-behalf-of case) or not (member's
// own token) — get this wrong and the *other* route's permission middleware
// 403s before the controller is ever reached, regardless of what the caller
// would otherwise be allowed to do inside store() itself.
// The read endpoints (`my-submissions`, `/{id}`, `/{id}/police-report`) don't
// have this split: the general `/api/id-card-reprint-requests/*` versions are
// gated only by `auth:sanctum` (ownership is checked inside the controller
// method itself), so both caller types already share those unchanged.
//
// Online (CyberExt) payment IS wired for reprints — IdCardReprintController::
// formatRequest() builds a real `payment_payload` via the same
// PaymentGatewayPayload::make() helper the registration flow uses, whenever a
// request is `approved` + payment `Pending` (`can_pay: true`). It's included
// automatically on the relevant rows from `submitIdCardReprintRequest`
// (auto-approve case), `listMyIdCardReprintRequests`, and
// `getIdCardReprintRequest` below, alongside a ready-made `payment_options`
// array (`card_payment` with `payment_payload`, `shroff_payment` with
// `shroff_voucher_url` + instructions) — mirroring the member/club
// registration payment_payload shape 1:1, so the same `openPaymentGateway()`
// (cyberGatewayService.js) call that drives PaymentMethod.jsx works here
// unchanged. The backend's gateway callback (POST /api/id-card-reprint-
// payments/confirm -> UnifiedPaymentConfirmController) is already wired too.
// There is no dedicated `/id-card-reprint-payments/status` polling route like
// registration/renewal have, though — poll `getIdCardReprintRequest(id)`
// instead (member-authorized) and watch `payment_status`.

const toFormData = (payload) => {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") formData.append(key, value);
  });
  return formData;
};

// POST /api/id-card-reprint-requests (system-user, member_id required) or
// POST /api/member/id-card-reprint-requests (portal member, no member_id —
// picked automatically based on whether `payload.member_id` is set). Both hit
// the same controller method with the same multipart body: { member_id
// (system-user callers only), reason, police_report (File, required —
// pdf/jpg/jpeg/png/webp, max 5MB) }. Bumps the member's card_version
// immediately (old QR denied right away) and creates the request as
// "Pending" approval — unless the caller already has
// "approve_id_card_reprint_requests" (Director/Admin submitting directly),
// in which case it's auto-approved and payable right away. Returns 201
// { message, reprint_request: {...same shape as one entry from
// listMyIdCardReprintRequests below} }.
export const submitIdCardReprintRequest = (payload) =>
  api
    .post(payload.member_id ? "/api/id-card-reprint-requests" : "/api/member/id-card-reprint-requests", toFormData(payload))
    .then((r) => r.data);

// GET /api/id-card-reprint-requests/my-submissions — portal-member only (403
// if the token doesn't belong to a member). Returns an array of this
// member's own reprint requests, newest first: {id, member_id, member_code,
// title, initials, lastname, email, nic_number, reason,
// police_report_file_url, previous_card_version, new_card_version, card_qr,
// approval_status_id, approval_status, rejection_note, payment_status_id,
// payment_status, payment_reference, external_payment_reference,
// payable_amount, paid_amount, paid_at, created_at, can_pay (bool — true once
// Director-approved and still payment-Pending), shroff_voucher_url (public
// applicant voucher link, only when can_pay), and — only when can_pay —
// payment_payload (real CyberExt gateway fields, ready for
// cyberGatewayService.openPaymentGateway) and payment_options (a 2-entry
// [{method:"card_payment", label, payment_payload}, {method:"shroff_payment",
// label, shroff_voucher_url, instructions}] array for a method-picker UI).
export const listMyIdCardReprintRequests = () =>
  api.get("/api/id-card-reprint-requests/my-submissions").then((r) => (Array.isArray(r.data) ? r.data : []));

// GET /api/id-card-reprint-requests/{id} — single row, same shape as one
// entry from listMyIdCardReprintRequests above (always includes
// payment_payload/payment_options when can_pay is true, unlike the list-only
// endpoints below which omit them). Works for either the owning portal
// member's token, or a system user with "view_id_card_reprint_approvals" /
// "view_payments" / "submit_id_card_reprint_requests" — used here to poll for
// payment_status flipping to "Paid" while a gateway popup is open, since
// there's no dedicated reprint payment-status route.
export const getIdCardReprintRequest = (id) => api.get(`/api/id-card-reprint-requests/${id}`).then((r) => r.data);

// GET /api/id-card-reprint-approvals — system-user, gated by
// "view_id_card_reprint_approvals" (Director/Admin only — Instructor does not
// have this). Only ever returns requests currently "Pending" approval; same
// row shape as listMyIdCardReprintRequests above.
export const listIdCardReprintApprovals = () =>
  api.get("/api/id-card-reprint-approvals").then((r) => (Array.isArray(r.data) ? r.data : []));

// POST /api/id-card-reprint-approvals/{id}/approve — { note? }. Gated by
// "approve_id_card_reprint_requests". Creates the payment_reference for this
// request, which is what flips it to `can_pay: true` on the member's own
// next fetch (listMyIdCardReprintRequests/getIdCardReprintRequest) — the
// member then picks Card or Shroff themselves via `payment_options`, same as
// registration. Returns { message, reprint_request: {...same shape as
// getIdCardReprintRequest, including payment_payload/payment_options} }.
export const approveIdCardReprintRequest = (id, note) =>
  api.post(`/api/id-card-reprint-approvals/${id}/approve`, note ? { note } : {}).then((r) => r.data);

// POST /api/id-card-reprint-approvals/{id}/reject — { rejection_note
// (required) }. Gated by "reject_id_card_reprint_requests". Returns
// { message, reprint_request: {...same shape as getIdCardReprintRequest} }.
export const rejectIdCardReprintRequest = (id, rejectionNote) =>
  api.post(`/api/id-card-reprint-approvals/${id}/reject`, { rejection_note: rejectionNote }).then((r) => r.data);

// GET /api/id-card-reprint-payments/{reprintId}/shroff-voucher — printable
// pay-at-counter voucher PDF for this reprint's fee, keyed by the reprint
// request's own numeric id (not payment_reference). Gated by "view_payments"
// (Instructor has this). responseType "blob" so axios doesn't try to
// JSON-parse the PDF.
export const getIdCardReprintShroffVoucherPdf = (reprintId) =>
  api
    .get(`/api/id-card-reprint-payments/${reprintId}/shroff-voucher`, { responseType: "blob" })
    .then((r) => r.data);

// POST /api/id-card-reprint-payments/manual-confirm — multipart (the shroff
// receipt scan is required, like every other manual-confirm route).
// payload: { payment_reference (required — the one from the approve
// response), paid_amount, paid_date, receipt_reference, receipt (File,
// required), note? }. Gated by
// "submit_manual_payment_receipts". Marks the payment Paid and flips
// card_status to "Pending Print" (surfacing the member in the existing print
// queue) immediately — HTTP 200, not a queued 202. A manual_payment_receipts
// row is still queued for Director/Admin acknowledgement on the existing
// Payments -> Manual Approvals tab (paymentService.js's
// listManualPaymentApprovals/approveManualPaymentApproval), but that step is
// audit-only now and doesn't gate the payment or the print queue.
export const confirmIdCardReprintManualPayment = (payload) =>
  api.post("/api/id-card-reprint-payments/manual-confirm", toManualPaymentFormData(payload)).then((r) => r.data);
